import { useAuth0 } from '@auth0/auth0-react';
import {
  Button,
  Center,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  Input,
  Skeleton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircleFilled } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { useRef, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { CountryCode } from 'libphonenumber-js';
import { useRender } from '@/utils/hooks';
import { ApiError, useEloApi } from '@/api/api';

import { validatePhoneNumber, isValidEmail } from '@/utils/validations';
import { PhoneInput } from '../PhoneInput/PhoneInput';
import { useError } from '@/contexts/ErrorContext';

import { User } from '@/api/users';
import { findEmailErrorInErrors, findPhoneErrorInErrors } from '@/utils/utils';

export interface NewPatientButtonProps {
  onFinish: () => any;
  opened: boolean;
  onClose: () => any;
}

export default function NewPatientButton({ onFinish, opened, onClose }: NewPatientButtonProps) {
  const { getAccessTokenSilently } = useAuth0();
  const api = useEloApi({ getAccessTokenSilently });
  const errorContext = useError();

  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [localCountryCode, setLocalCountryCode] = useState<string>();
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const processing = useRef<boolean>(false);
  const [feedbackOpened, { open: feedbackOpen, close: feedbackClose }] = useDisclosure(false);
  const render = useRender();
  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  const [localUser, setLocalUser] = useState<User | undefined>(undefined);
  const getUser = async () => {
    setLoading(true);

    try {
      const res = await api.getUser();
      setLocalUser({ ...res });
    } catch (error) {
      let errorMessage;
      if (error instanceof ApiError) {
        errorMessage = error.message;
      }
      errorContext.setError(errorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUser();
  }, []);

  return (
    <>
      <Modal opened={opened} onClose={onClose} title={t('new_patient_button.new_patient')}>
        <TextInput
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          label={t('new_patient_button.name')}
          placeholder="John Smith"
          error={nameError ? t('new_patient_button.name_error') : null}
          mb="md"
        />
        <div>
          <TextInput
            value={email}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
              setEmailError(
                !isValidEmail(event.currentTarget.value) ? t('new_patient_button.email_error') : ''
              );
            }}
            label={t('new_patient_button.email')}
            placeholder="john.doe@example.com"
            error={emailError}
          />
        </div>
        <Skeleton visible={loading}>
          {localUser ? (
            <PhoneInput
              label={t('new_patient_button.phone')}
              defaultCallingCode={localUser?.defaultCountryCode || undefined}
              onChange={(value, callingCode, isValid) => {
                setPhone(value);
                setLocalCountryCode(callingCode);
                setPhoneError((current) => (!isValid ? '' : current));
              }}
              defaultError={phoneError}
            />
          ) : (
            <div>Loading</div>
          )}
        </Skeleton>

        <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1rem' }}>
          <Button
            onClick={async () => {
              setNameError(!name);

              if (!name) {
                return;
              }
              if (processing.current) return;
              processing.current = true;

              try {
                await api.patientsCreate({ name, email, phone, countryCode: localCountryCode });
                toast.success(`¡Se ha creado al paciente ${name} exitosamente!`, {
                  duration: 8000,
                });
                setName('');
                setEmail('');
                setPhone('');

                close();
                onFinish();
              } catch (e) {
                if (e instanceof ApiError) {
                  // const isEmailError = e.errors[0].hasOwnProperty('email');
                  // const isPhoneError = e.errors[0].hasOwnProperty('phone');
                  const isEmailError = findEmailErrorInErrors(e.errors);
                  const isPhoneError = findPhoneErrorInErrors(e.errors);
                  // if (isEmailError) toast.error(e.errors[0]?.email, { duration: 8000 });
                  // else if (isPhoneError) toast.error(e.errors[0].phone, { duration: 8000 });

                  if (isEmailError) setEmailError(isEmailError);
                  if (isPhoneError) setPhoneError(isPhoneError);

                  //feedbackOpen();
                } else {
                  throw e;
                }
              } finally {
                processing.current = false;
              }
            }}
            loading={processing.current}
          >
            {t('new_patient_button.create')}
          </Button>
        </div>
      </Modal>

      {/*
      <Modal
        opened={feedbackOpened}
        onClose={() => {
          feedbackClose();
          onFinish();
        }}
        withCloseButton={false}
      >
        <Center>
          <Stack justify="center" align="center">
            <IconAlertCircleFilled size={128} color="var(--mantine-color-red-7)" />
            <Title order={2}>{t('new_patient_button.failed')}</Title>
            <Text>{errorText}</Text>
          </Stack>
        </Center>
      </Modal>
      */}
    </>
  );
}
